import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { withConflictConfirm } from '../availability/conflicts';
import { api, ApiError, fieldIssues } from '../api';
import { AddPricedLine, type AddGear, type ReplacedLine } from './AddPricedLine';
import { OTHER_TITLE, useFixedServices } from './fixedServices';
import type { QuoteLine, Section } from './types';

/** Clicks that do not cancel the change: the field itself, its lists and menus, the question „Dodaj mimo to”. */
const KEEPS = '[role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"]';

/**
 * Owner 2026-10-24 16:15 („Jak klikam w wybrana juz pozycje w kosztorysie to niech sie otwiera mozliwosc jej zmiany :)
 * na inna czyli tak samo jakby to byla pusta pozycja”): in place of a line's name, what the section's empty row offers —
 * in an equipment section the "Dodaj…" field (with its ⚙, the suppliers' positions and an own line), in „Obsługa” /
 * „Transport” the price list's services and „Inne”. The pick replaces the line where it stands (`POST
 * /api/quotes/lines/:id/replace`); Esc or a click outside cancels and nothing changes.
 */
export function ReplaceLine({
  line,
  section,
  versionId,
  gear,
  onReplaced,
  onCancel,
}: {
  line: QuoteLine;
  section: Pick<Section, 'id' | 'name' | 'kind'> & { categoryId?: string | null | undefined };
  versionId: string;
  gear?: AddGear | undefined;
  /** `other`: „Inne” was picked — its name takes the focus (as when added). */
  onReplaced: (line: ReplacedLine, other: boolean) => void;
  onCancel: () => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const cancel = useRef(onCancel);
  cancel.current = onCancel;
  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      if (e.composedPath().some((n) => n === box.current || (n instanceof Element && n.matches(KEEPS)))) return;
      cancel.current();
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, []);
  // Esc cancels at once (the list, the text and the selection of the table stay as they were)
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Escape' || (e.target instanceof Element && e.target.closest('[role="menu"]:not(.q-replace-menu)'))) return;
    e.preventDefault();
    e.stopPropagation();
    onCancel();
  };
  return (
    <div className="q-replace-box" ref={box} onKeyDownCapture={onKeyDown} data-feature-id="quotes.replace_line">
      {section.kind === 'equipment' ? (
        <AddPricedLine
          versionId={versionId}
          sectionId={section.id}
          sectionName={section.name}
          categoryId={section.categoryId ?? null}
          onAdded={() => undefined}
          gear={gear}
          replace={{ lineId: line.id, version: line.version, lineName: line.name, onReplaced: (l) => onReplaced(l, false) }}
        />
      ) : (
        <FixedReplace line={line} transport={section.kind === 'transport'} versionId={versionId} onReplaced={onReplaced} />
      )}
    </div>
  );
}

/** „Obsługa” / „Transport”: the services of the section and „Inne”, as its „+ Usługa” / „+ Transport” lists them. */
function FixedReplace({ line, transport, versionId, onReplaced }: { line: QuoteLine; transport: boolean; versionId: string; onReplaced: (line: ReplacedLine, other: boolean) => void }) {
  const { ready, list, labelOf } = useFixedServices(versionId, transport);
  const [error, setError] = useState('');
  const menu = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (ready) menu.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus();
  }, [ready]);
  async function pick(body: Record<string, unknown>, other: boolean) {
    try {
      const done = await withConflictConfirm((confirmConflicts) => api<ReplacedLine>(`/api/quotes/lines/${line.id}/replace`, { method: 'POST', body: { ...body, version: line.version, ...(confirmConflicts ? { confirmConflicts } : {}) } }));
      if (done) onReplaced(done, other);
    } catch (e) {
      setError(e instanceof ApiError ? (fieldIssues(e)[0]?.message ?? e.message) : 'Nie udało się zmienić pozycji');
    }
  }
  // ↑ / ↓ between the services (Tab moves on as usual)
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const items = [...(menu.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? [])];
    const at = items.indexOf(document.activeElement as HTMLButtonElement);
    items[(at + (e.key === 'ArrowDown' ? 1 : items.length - 1)) % items.length]?.focus();
  };
  return (
    <>
      <div className="q-replace-menu" role="menu" aria-label={`Zmień pozycję „${line.name}” na inną`} ref={menu} onKeyDown={onKeyDown}>
        {!ready && <span className="hint">Wczytywanie…</span>}
        {list.map((h) => (
          <button key={h.id} type="button" role="menuitem" aria-current={h.id === line.serviceId ? 'true' : undefined} onClick={() => void pick({ type: 'service', serviceId: h.id }, false)}>
            {labelOf(h)}
          </button>
        ))}
        {ready && (
          <button type="button" role="menuitem" title={OTHER_TITLE} onClick={() => void pick({ type: 'custom', name: 'Inne' }, true)}>
            Inne
          </button>
        )}
      </div>
      {error && (
        <span className="field-error" role="alert">
          {error}
        </span>
      )}
    </>
  );
}
